<template>
  <div class="c--shared_ekyc-detail ekyc-detail-wrapper">
    <p class="ekyc-detail-title">{{ title }}</p>
    <div class="ekyc-detail-card-and-learn-more overflow-hidden">
      <div class="ekyc-detail-card">
        <div class="d-flex align-items-center">
          <i :class="[kycDocumentNameAndIcon.icon, 'ekyc-detail-identity-card']" />
          <p class="ekyc-detail-identity-card-title">
            {{ kycDocumentNameAndIcon.text }}
          </p>
        </div>
        <p class="ekyc-detail-identity-name">
          {{ identitiesDefault.full_name || '***** *****' }}
        </p>
        <h1 :class="`ekyc-detail-verify-status ${verifyClass}`">
          {{ kycStatusText }}
        </h1>
        <p
          class="ekyc-detail-expires-date"
          v-if="!['rejected', 'pending'].includes(identitiesDefault.status) && this.identitiesDefault.expiry"
        >
          <span v-html="generateExpiryDate" />
        </p>
        <div v-if="isShowLottie">
          <Lottie
            :id="`ekyc-detail-identity-lottie`"
            :ref="`ekyc-detail-identity-lottie`"
            :style="`width: 60px; height: 17px;`"
            :options="{
              loop: true,
              autoplay: true,
              animationData: identitiesDefault.status === 'rejected' ? rejectedLottie : verifyingLottie,
              rendererSettings: false,
            }"
            :width="60"
            :height="17"
            class="player-item display-flex"
          />
        </div>
        <img
          v-if="identitiesDefault.is_kyc_one_time"
          class="ekyc-detail-card-operator-img"
          width="70"
          height="44"
          :src="identitiesDefault.packages.operator.image.url"
          :class="{
            topRight: identitiesDefault.is_kyc_one_time,
          }"
          alt=""
          loading="lazy"
        />
        <i
          id="ekyc-detail-card-info"
          class="airalo-icon-exclamation-mark-round tw-text-white ekyc-detail-card-info"
          :class="{
            topRightInfo: !identitiesDefault.is_kyc_one_time,
            bottomRight: identitiesDefault.is_kyc_one_time,
          }"
        />
        <Tooltip
          target="ekyc-detail-card-info"
          :text="ekycDetailTooltipText()"
          :button-text="$t('components.tooltip.got-it-button')"
          :is-clone-target="true"
        />
        <a-icon type="image" loader="data" name="icon-ekyc-detail-finger-print" class="ekyc-detail-finger-print" />
      </div>
      <div class="ekyc-detail-learn-more-right">
        <template v-if="identitiesDefault.status === 'rejected' && identitiesDefault.rejection_reason_translated">
          <div class="ekyc-detail-learn-more-rejection">
            <p class="ekyc-detail-learn-more-rejection-title">
              {{ $t('scenes.identity.authentication.rejection-reason-title') }}
            </p>
            <p class="ekyc-detail-learn-more-rejection-desc">
              {{ identitiesDefault.rejection_reason_translated }}
            </p>
          </div>
        </template>

        <p class="ekyc-detail-learn-more-title">
          {{ $t('scenes.identity.authentication.description-title.v2') }}
        </p>

        <i18n
          path="scenes.identity.authentication.description-subtitle.v2"
          tag="p"
          class="ekyc-detail-learn-more-description-subtitle tw-mb-0"
        >
          <template #goToHelpCenter>
            <a :href="$t('url.identity.authentication.help-center')" target="_blank" rel="noopener">
              {{ $t('scenes.identity.authentication.go-to-help-center.v2') }}
            </a>
          </template>
        </i18n>
      </div>
    </div>
    <div
      v-if="[null, 'rejected', 'expired'].includes(identitiesDefault.status)"
      class="ekyc-detail-verify-btn std-alone-sized-larger btn-primary-hv"
      data-testid="eKYC-button"
      @click="toCompleteKyc()"
    >
      <span>{{ $t('global.ekyc-procced-resubmit') }}</span>
      <KYCVerifyModal />
    </div>
  </div>
</template>

<script>
  import { differenceInMinutes } from 'date-fns'
  import verifyingLottie from '~/assets/animation/verifying.json'
  import rejectedLottie from '~/assets/animation/rejected.json'
  import Tooltip from '~/components/shared/overlay-tooltip.vue'
  import KYCVerifyModal from '~/components/shared/kyc-verify-modal/jumio.vue'
  import Lottie from 'vue-lottie/src/lottie.vue'

  export default {
    name: 'ekyc-detail',
    components: {
      Tooltip,
      KYCVerifyModal,
      Lottie,
    },
    props: {
      title: {
        type: String,
        required: true,
      },
      identitiesDefault: {
        type: [Object, Array],
        required: true,
        // status: -> pending, approved, rejected, expired, not-verified, unknown, used
        // documentType: -> "", DRIVING_LICENSE, ID_CARD, VISA, PASSPORT, UNSUPPORTED, unknown
      },
    },
    data() {
      return {
        isShowLottie: false,
        verifyingLottie: verifyingLottie,
        rejectedLottie: rejectedLottie,
      }
    },
    computed: {
      verifyClass() {
        if (this.identitiesDefault.status === 'approved') {
          return 'verified'
        } else if (['rejected', 'expired'].includes(this.identitiesDefault.status)) {
          return 'no-verify'
        }

        return ''
      },
      kycDocumentNameAndIcon() {
        if (this.identitiesDefault.documentType === 'DRIVING_LICENSE') {
          return {
            icon: 'airalo-icon-car-round tw-text-white',
            text: this.$t('scenes.identity.authentication.driver-license'),
          }
        } else if (this.identitiesDefault.documentType === 'ID_CARD') {
          return {
            icon: 'airalo-icon-identity-card tw-text-white',
            text: this.$t('scenes.identity.authentication.identity-card'),
          }
        } else if (this.identitiesDefault.documentType === 'VISA') {
          return {
            icon: 'airalo-icon-globe-1 tw-text-white',
            text: this.$t('scenes.identity.authentication.visa'),
          }
        } else if (this.identitiesDefault.documentType === 'PASSPORT') {
          return {
            icon: 'airalo-icon-globe-1 tw-text-white',
            text: this.$t('scenes.identity.authentication.passport'),
          }
        } else {
          return {
            icon: 'airalo-icon-globe-1 tw-text-white',
            text: this.$t('scenes.identity.authentication.unknown-document'),
          }
        }
      },
      kycStatusText() {
        if (this.identitiesDefault.status === 'approved') {
          return this.$t('scenes.identity.authentication.verified')
        } else if (this.identitiesDefault.status === 'rejected') {
          return this.$t('scenes.identity.authentication.rejected')
        } else if (this.identitiesDefault.status === 'expired') {
          return this.$t('scenes.identity.authentication.expired')
        } else if (this.identitiesDefault.status === 'unknown') {
          return this.$t('scenes.identity.authentication.unknown-state')
        } else if (this.identitiesDefault.status === 'pending') {
          return this.$t('scenes.identity.authentication.verifiying')
        } else {
          return this.$t('scenes.identity.authentication.not-verified')
        }
      },
      generateExpiryDate() {
        let expiresIn = null
        const newDateWithUTCZero = new Date().toLocaleString(this.$i18n.locale, { timeZone: 'Greenwich' })

        const expiryDiffInMin = differenceInMinutes(
          new Date(this.identitiesDefault.expiry).getTime(),
          new Date(newDateWithUTCZero).getTime()
        )

        const expiryText =
          this.identitiesDefault.status === 'approved' ? this.$t('global.expires-on') : this.$t('global.expired-at')
        const expiryDate = this.identitiesDefault.expiry
          ? this.$dateFormat(this.identitiesDefault.expiry, 'DD MMM yyyy')
          : 'N/A'

        const expiryDiffHour = expiryDiffInMin < 60 ? 0 : Math.round(expiryDiffInMin / 60)
        if (expiryDiffInMin > 0 && expiryDiffHour <= 24) {
          if (expiryDiffHour > 1) {
            expiresIn = this.$t('scenes.ekyc.detail.expires-in-hours', {
              eKycDuration: expiryDiffHour,
            })
          } else if (expiryDiffHour === 1) {
            expiresIn = this.$t('scenes.ekyc.detail.expires-in-hour', {
              eKycDuration: expiryDiffHour,
            })
          } else {
            expiresIn = this.$t('scenes.ekyc.detail.expires-in-minutes', {
              eKycDuration: expiryDiffInMin,
            })
          }
        }

        return (expiresIn || expiryText + ' ' + expiryDate).toUpperCase()
      },
    },
    created() {
      setTimeout(() => {
        this.isShowLottie =
          ['rejected', 'pending'].includes(this.identitiesDefault.status) && !this.identitiesDefault.expiry
      }, 500)
    },
    methods: {
      ekycDetailTooltipText() {
        if (this.identitiesDefault.is_kyc_one_time) {
          const operatorName = this.identitiesDefault.packages.operator.title
          if (this.identitiesDefault.status === 'approved') {
            return this.$t('components.identity.authentication.tooltip.one-time-verified', { operatorName })
          } else if (this.identitiesDefault.status === 'rejected') {
            return this.$t('components.identity.authentication.tooltip.one-time-rejected', { operatorName })
          } else if (this.identitiesDefault.status === 'expired') {
            return this.$t('components.identity.authentication.tooltip.one-time-expired')
          } else {
            return this.$t('components.identity.authentication.tooltip.one-time-verifying', { operatorName })
          }
        } else {
          if (this.identitiesDefault.status === 'approved') {
            return this.$t('components.identity.authentication.tooltip.default-verified')
          } else if (this.identitiesDefault.status === 'rejected') {
            return this.$t('components.identity.authentication.tooltip.default-rejected')
          } else if (this.identitiesDefault.status === 'expired') {
            return this.$t('components.identity.authentication.tooltip.default-expired')
          } else {
            return this.$t('components.identity.authentication.tooltip.default-verifying')
          }
        }
      },
      toCompleteKyc() {
        const operator = this.identitiesDefault?.packages?.operator || null
        const package_id = operator?.is_kyc_one_time ? this.identitiesDefault.packages.id : undefined
        const isOneTime = !!package_id

        const privacyInfo = {
          kyc_type: operator?.kyc_type || 'no_agreement',
          privacy_policy_url: operator?.privacy_policy_url || '',
          operatorName: operator?.title || '',
          checkbox: false,
        }

        this.$store.commit('eKyc/SET_VERIFY_MODAL_DATA', {
          isShow: true,
          privacyInfo,
          package_id,
          isOneTime,
          kycProvider: this.identitiesDefault?.provider || 'jumio',
          isDestinationAddressRequired: operator?.kyc_addr_required,
        })
      },
    },
  }
</script>

<style lang="scss" scoped>
  .ekyc-detail-wrapper {
    display: flex;
    flex-direction: column;
    .ekyc-detail-title {
      color: #4a4a4a;
      font-size: 27px;
      font-weight: 600;
      letter-spacing: -0.5px;
      line-height: 32px;
      margin-bottom: 20px;
    }
    .ekyc-detail-card-and-learn-more {
      display: flex;
      .ekyc-detail-card {
        padding: 20px;
        position: relative;
        min-width: 350px;
        height: 180px;
        border-radius: 7px;
        background: linear-gradient(90deg, #121212 0%, #0d324d 100%);
        box-shadow: 0 10px 30px 0 rgba(0, 0, 0, 0.15);

        @media only screen and (max-width: 414px) {
          min-width: 280px;
        }

        .ekyc-detail-card-info {
          cursor: pointer;
        }
        .ekyc-detail-card-operator-img {
          border-radius: 4px;
        }
        .ekyc-detail-card-info,
        .ekyc-detail-card-operator-img {
          z-index: 3;
          content: '';
          position: absolute;
          right: 10px;
          [dir='rtl'] & {
            right: unset;
            left: 10px;
          }
          &.topRight {
            top: 20px;
          }
          &.topRightInfo {
            top: 10px;
          }
          &.bottomRight {
            bottom: 20px;
          }
        }
        .ekyc-detail-finger-print {
          content: '';
          position: absolute;
          right: -25px;
          bottom: -10px;
          z-index: 2;
        }
        .ekyc-detail-identity-card-title {
          color: #ffffff;
          font-size: 13px;
          font-weight: 500;
          letter-spacing: 0;
          line-height: 15px;
          margin: 0 10px 0 10px;
        }
        .ekyc-detail-identity-name {
          color: #ffffff;
          font-size: 17px;
          font-weight: 500;
          letter-spacing: -0.1px;
          line-height: 20px;
          margin-bottom: 0;
          margin-top: 10px;
        }
        .ekyc-detail-verify-status {
          margin-top: 20px;
          color: #ffffff;
          font-size: 27px;
          font-weight: 600;
          letter-spacing: -0.5px;
          line-height: 32px;
          margin-bottom: 20px;

          &.verified {
            color: #7ec027;
          }
          &.no-verify {
            color: #f2222e;
          }
        }
        .ekyc-detail-expires-date {
          color: #ffffff;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 1px;
          line-height: 14px;
          margin-bottom: 0;
        }
      }
      .ekyc-detail-learn-more-right {
        margin-left: 30px;
        [dir='rtl'] & {
          margin-left: unset;
          margin-right: 30px;
        }
        .ekyc-detail-learn-more-title {
          margin-bottom: 10px;
        }
        .airalo-icon-wrapper {
          margin-top: 10px;
        }
        .ekyc-detail-learn-more-rejection {
          margin-bottom: 20px;

          &-title {
            color: #4a4a4a;
            font-size: 15px;
            font-weight: 500;
            letter-spacing: 0;
            line-height: 20px;
            margin-bottom: 10px;
          }
          &-desc {
            color: #8a8a8a;
            font-size: 13px;
            letter-spacing: 0;
            line-height: 18px;
            margin-bottom: 0;
          }
        }
        .ekyc-detail-learn-more-description-subtitle {
          color: #8a8a8a;
          font-size: 13px;
          letter-spacing: 0;
          line-height: 18px;
          ::v-deep a {
            font-weight: 600;
          }
        }
      }

      @media only screen and (max-width: 768px) {
        flex-direction: column;

        .ekyc-detail-learn-more-right {
          margin-top: 20px;
          margin-left: 0;
        }
      }
    }

    .ekyc-detail-learn-more-title {
      color: #4a4a4a;
      font-size: 15px;
      font-weight: 500;
      letter-spacing: 0;
      line-height: 20px;
      margin-bottom: 0;
    }
    .ekyc-detail-verify-btn {
      margin: 40px auto;
      padding: 17px 20px 16px;
      border-radius: 7px;
      background-color: #4a4a4a;
      text-align: center;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;

      &:hover {
        background: #fff;
        color: #000;
      }

      span {
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 1px;
        line-height: 11px;
        text-align: center;
      }
    }
  }
  .ekyc-detail-modal ::v-deep .ekyc-detail-verify-btn {
    margin-bottom: 0;
  }
</style>
